import { useMemo } from 'react';
import { getSessionToken, useSession } from '@descope/react-sdk';
import { DescopeSessionClaims, readDescopeSessionClaims } from '@packages/core-shared';

/**
 * The claims of the signed-in user's Descope session token - the identity and
 * the permissions the API authorizes the very same token on (PPL-3826).
 *
 * Deliberately NOT `useSession().claims`: that object is whatever the `claims`
 * field of an auth response body held, which carries neither the role-derived
 * `permissions` nor the flow's `name`, and nothing at all until a response
 * arrives. Reading the token instead is what `ApiRepository` already does for
 * the Authorization header, so the UI gates on exactly what it sends.
 */
export const useSessionClaims = (): DescopeSessionClaims | undefined => {
  // The SDK publishes the token through the context on every change, so this
  // re-renders whenever the session is refreshed or replaced.
  const { sessionToken } = useSession();
  // It is empty until the SDK's first auth response, while the persisted
  // token is already readable from storage - so on a reload take that one and
  // let nothing depend on which of the two arrives first.
  const token = sessionToken ? sessionToken : getSessionToken();

  return useMemo(() => readDescopeSessionClaims(token), [token]);
};
